import { useEffect } from 'preact/hooks';
import type { CalloutMode } from '@/bridge/gameBus';
import { gameSignals } from '@/bridge/gameSignals';
import { t } from '@/i18n';
import { audio } from '@/audio-singleton';
import { ARCHETYPES, archetypeTier } from '@/lib/archetypes';
import { DANGER_ZONE_THRESHOLD } from '@/lib/constants';
import type { Zone } from '@/lib/zones';
import { Medallion } from './Medallion';

const ZONES_BY_GRID: readonly { zone: Zone; col: 0 | 1 | 2; row: 0 | 1 }[] = [
  { zone: 'LA', col: 0, row: 0 },
  { zone: 'CA', col: 1, row: 0 },
  { zone: 'RA', col: 2, row: 0 },
  { zone: 'LF', col: 0, row: 1 },
  { zone: 'CF', col: 1, row: 1 },
  { zone: 'RF', col: 2, row: 1 },
];

interface ArchetypeCalloutProps {
  mode?: CalloutMode;
  observations?: Record<Zone, number>;
  pulseZone?: Zone | null;
  /** Tightens spacing + shrinks hero zone for fit-in-viewport on RESULT scene. */
  compact?: boolean;
}

export function ArchetypeCallout({ mode, observations, pulseZone, compact = false }: ArchetypeCalloutProps = {}) {
  const a = gameSignals.archetype.value;
  const num = gameSignals.puzzleNumber.value;
  if (!a) return null;
  const meta = ARCHETYPES.find((x) => x.id === a.id);
  if (!meta) return null;

  const effMode: CalloutMode = mode ?? gameSignals.cardMode.value;

  useEffect(() => {
    if (effMode === 'truth') audio().play('reveal');
  }, [effMode]);

  const effObs: Record<Zone, number> = observations ?? gameSignals.observations.value;
  const effPulse: Zone | null = pulseZone ?? gameSignals.pulseZone.value;

  const tier = archetypeTier(meta);
  const stars = '★'.repeat(tier) + '☆'.repeat(3 - tier);
  const tierLabel = t(`archetype.difficulty.${tier}` as never);
  const persona = t(`archetype.${a.id}.persona` as never);
  const initials = persona
    .split(' ')
    .map((p) => p[0])
    .join('')
    .slice(0, 2);
  const serial = num !== null && num > 0
    ? t('archetype.cardSerial', { n: String(num).padStart(3, '0') })
    : t('archetype.cardSerial', { n: '???' });

  // Only consumed in the non-truth branch below; truth mode renders the
  // per-keeper takeaway sentence instead.
  const hintKey =
    effMode === 'preplay' ? 'archetype.heatmapHintPreplay' : 'archetype.heatmapHintLive';

  return (
    <section class={`mx-auto ${compact ? 'mt-3' : 'mt-5'} max-w-[22rem] px-4 card-deal card-deal-2`}>
      <div class="keeper-card">
        <div class="keeper-card-inner">
          {/* TOP STRIP: jersey + flag · tier */}
          <div class={`relative z-10 flex items-center justify-between px-4 ${compact ? 'pt-2' : 'pt-3'}`}>
            <div class="flex items-center gap-2.5">
              <span class="font-display text-base text-foil leading-none">#1</span>
              <HuFlag />
            </div>
            <div class="flex flex-col items-end leading-none">
              <span class="tier-stars text-foil text-xs">{stars}</span>
              <span class="font-mono text-[8px] uppercase tracking-[0.2em] text-foil/80 mt-1">
                {tierLabel}
              </span>
            </div>
          </div>

          {/* HERO ZONE — foil medallion with persona initials */}
          <div class={`relative z-10 mx-4 mt-2 ${compact ? 'h-24' : 'h-36'} rounded-md overflow-hidden bg-gradient-to-b from-bg-2/60 via-card to-card-2/80 border border-foil/15`}>
            <div
              class="absolute inset-0 opacity-50"
              style={{
                background:
                  'radial-gradient(ellipse at 50% 30%, rgba(34,211,238,0.18), transparent 65%), repeating-linear-gradient(45deg, transparent, transparent 8px, rgba(251,191,36,0.04) 8px, rgba(251,191,36,0.04) 9px)',
              }}
              aria-hidden="true"
            />
            <Medallion initials={initials} />
          </div>

          {/* PERSONA NAME + NICKNAME */}
          <div class={`relative z-10 px-5 ${compact ? 'mt-3' : 'mt-4'} text-center`}>
            <h2 class={`font-display ${compact ? 'text-xl' : 'text-2xl'} leading-tight text-text uppercase`}>
              {persona}
            </h2>
            <p class="mt-0.5 italic text-sm text-foil">
              „{t(`archetype.${a.id}.name` as never)}"
            </p>
            {!compact && (
              <p class="mt-1 text-xs text-text-2">
                {t(`archetype.${a.id}.hint` as never)}
              </p>
            )}
          </div>

          {/* DECORATIVE DIVIDER */}
          <div class={`relative z-10 ${compact ? 'mt-3' : 'mt-4'} mx-5 flex items-center gap-2`}>
            <span class="flex-1 h-px bg-gradient-to-r from-transparent via-foil/40 to-transparent" />
            <span class="text-foil/70 text-[8px]">◆</span>
            <span class="flex-1 h-px bg-gradient-to-r from-transparent via-foil/40 to-transparent" />
          </div>

          {/* DEFENSE GOAL-MAP — visual goal shape with 6 cells coloured by mode */}
          <div class={`relative z-10 px-5 mt-3`}>
            {effMode === 'truth' && (
              <p class="font-mono text-[9px] uppercase tracking-[0.22em] text-text-dim mb-2 text-center">
                {t('archetype.truthLabelTop')}
              </p>
            )}
            <GoalHeatmap
              dist={meta.distribution}
              mode={effMode}
              observations={effObs}
              pulseZone={effPulse}
              compact={compact}
            />
            {effMode === 'truth' ? (
              <p class="mt-2 text-center text-[11px] leading-snug text-text">
                {t(`archetype.${a.id}.truthTakeaway` as never)}
              </p>
            ) : (
              <p class="mt-2 text-center text-[9px] text-text-dim italic">
                {t(hintKey as never)}
              </p>
            )}
          </div>

          {/* BOTTOM STRIP — edition + serial */}
          <div class={`relative z-10 ${compact ? 'mt-3 mx-4 mb-2 pt-2' : 'mt-4 mx-4 mb-3 pt-2'} flex items-center justify-between border-t border-foil/15 font-mono text-[9px] uppercase tracking-[0.22em] text-text-dim`}>
            <span>{t('archetype.season')}</span>
            <span class="text-foil/80">{serial}</span>
            <span>napi11es</span>
          </div>
        </div>
      </div>
    </section>
  );
}

/**
 * Goal-shaped 3×2 heat-map showing where the keeper defends.
 * Renders one of three modes: preplay (all hatched ?), live (hatched until
 * observed, then count-tinted), truth (% with intensity tint + danger ring).
 * ARIA: `role="grid"` with two `role="row"` children of three `role="gridcell"`.
 */
function GoalHeatmap({
  dist,
  mode,
  observations,
  pulseZone,
  compact = false,
}: {
  dist: Record<Zone, number>;
  mode: CalloutMode;
  observations: Record<Zone, number>;
  pulseZone: Zone | null;
  compact?: boolean;
}) {
  const max = Math.max(...Object.values(dist));
  const cellW = compact ? 66 : 70;
  const gap = 2;
  const totalW = cellW * 3 + gap * 2;
  const topRow = ZONES_BY_GRID.filter((c) => c.row === 0);
  const bottomRow = ZONES_BY_GRID.filter((c) => c.row === 1);

  return (
    <div class="relative mx-auto" role="grid" style={{ maxWidth: `${totalW + 12}px` }}>
      {/* Goal posts hint (decorative goal-frame, preserved) */}
      <div
        class="absolute -inset-x-1.5 -top-1 h-1 border-t border-l border-r border-foil/40 rounded-t-sm"
        aria-hidden="true"
      />
      {/* Crossbar */}
      <div class="absolute -inset-x-1.5 -top-1 h-px bg-foil/40" aria-hidden="true" />

      <div role="row" class="grid grid-cols-3 gap-0.5">
        {topRow.map(({ zone }) => (
          <Cell
            key={zone}
            zone={zone}
            pct={dist[zone]}
            max={max}
            mode={mode}
            count={observations[zone] ?? 0}
            pulsing={pulseZone === zone}
          />
        ))}
      </div>
      <div role="row" class="grid grid-cols-3 gap-0.5 mt-0.5">
        {bottomRow.map(({ zone }) => (
          <Cell
            key={zone}
            zone={zone}
            pct={dist[zone]}
            max={max}
            mode={mode}
            count={observations[zone] ?? 0}
            pulsing={pulseZone === zone}
          />
        ))}
      </div>

      {/* Goal-line below */}
      <div class="absolute -inset-x-1.5 -bottom-1 h-px bg-foil/30" aria-hidden="true" />
    </div>
  );
}

function Cell({
  zone,
  pct,
  max,
  mode,
  count,
  pulsing,
}: {
  zone: Zone;
  pct: number;
  max: number;
  mode: CalloutMode;
  count: number;
  pulsing: boolean;
}) {
  const norm = pct / max;
  const isHiddenOrPreplay = mode === 'preplay' || (mode === 'live' && count === 0);
  const showNumeric = mode === 'truth';
  const showCount = mode === 'live' && count > 0;
  const isDangerZone = mode === 'truth' && pct >= DANGER_ZONE_THRESHOLD;

  const positionLabel = t(`archetype.position.${zone}` as never);
  const aria = showNumeric
    ? t('archetype.cellTruthA11y', { position: positionLabel, pct: String(Math.round(pct * 100)) })
    : isHiddenOrPreplay
      ? t('archetype.cellHiddenA11y', { position: positionLabel })
      : t('archetype.cellSeenA11y', { position: positionLabel, count: String(count) });

  // Live-mode tint by visit count; truth-mode tint by distribution intensity.
  const fillOpacity = showNumeric
    ? (0.35 + norm * 0.55).toFixed(2)
    : count >= 3
      ? '0.75'
      : count === 2
        ? '0.50'
        : '0.30';

  return (
    <div
      role="gridcell"
      data-zone={zone}
      data-danger={isDangerZone ? 'true' : undefined}
      data-pulsing={pulsing ? 'true' : undefined}
      aria-label={aria}
      class={`relative rounded-sm overflow-hidden border ${isDangerZone ? 'border-foil/80' : 'border-foil/15'} ${pulsing ? 'animate-pulse-goal' : ''}`}
      style={{
        aspectRatio: '70 / 36',
        ...(isDangerZone
          ? { boxShadow: '0 0 0 1px rgba(251,191,36,0.55), 0 0 10px rgba(251,191,36,0.3)' }
          : {}),
      }}
    >
      {isHiddenOrPreplay && (
        <div
          class="absolute inset-0 bg-bg-2"
          style={{
            background:
              'repeating-linear-gradient(45deg, transparent 0 4px, rgba(251,191,36,0.08) 4px 5px), var(--color-bg-2)',
          }}
          aria-hidden="true"
        />
      )}
      {!isHiddenOrPreplay && (
        <div
          class="stat-fill absolute inset-0 bg-gradient-to-br from-stat to-stat-deep"
          style={{
            opacity: fillOpacity,
            '--pct': '1',
          } as Record<string, string>}
        />
      )}
      <span class="relative z-10 flex h-full items-center justify-center font-mono text-[11px] font-medium text-text">
        {showNumeric && `${Math.round(pct * 100)}%`}
        {!showNumeric && (isHiddenOrPreplay ? '?' : '')}
      </span>
      {showCount && (
        <span class="absolute right-1 top-0.5 font-mono text-[8px] text-foil">
          {t('archetype.cellObservedCount', { count: String(count) })}
        </span>
      )}
    </div>
  );
}

function HuFlag() {
  return (
    <svg viewBox="0 0 18 12" class="h-3 w-[18px] rounded-[1px] overflow-hidden" aria-label="Magyarország">
      <rect x="0" y="0"  width="18" height="4" fill="#ce2939" />
      <rect x="0" y="4"  width="18" height="4" fill="#ffffff" />
      <rect x="0" y="8"  width="18" height="4" fill="#477050" />
    </svg>
  );
}
